import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { PlatformToken } from "@shared/schema";

export const DEFAULT_PLATFORM_STATS = {
  techLeaders: { value: "10,000+", label: "Tech Leaders" },
  capitalConnected: { value: "$850M+", label: "Raised by Startups" },
  countries: { value: "100+", label: "Countries" },
  speakers: { value: "700+", label: "Speakers" },
  monthlyReaders: { value: "100K+", label: "Monthly Readers" },
  industries: { value: "50+", label: "Industries" },
  founders: { value: "1,000+", label: "Founders" },
  investors: { value: "500+", label: "Investors" },
  executives: { value: "2,500+", label: "Executives" },
  articles: { value: "500+", label: "Articles Published" },
  podcasts: { value: "10+", label: "Podcast Episodes" },
  summitAttendees: { value: "5,000+", label: "Summit Attendees" },
  bookContributors: { value: "50+", label: "Book Contributors" },
};

export function usePlatformTokens() {
  const { data: tokens, isLoading, isError } = useQuery<PlatformToken[]>({
    queryKey: ["/api/platform-tokens"],
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const platformStats = useMemo(() => {
    if (isLoading || isError || !tokens || tokens.length === 0) {
      return DEFAULT_PLATFORM_STATS;
    }

    const getTokenValue = (name: string, defaultValue: string): string => {
      const token = tokens.find(t => t.name === name && t.isActive);
      return token?.staticValue || defaultValue;
    };

    const getStatValue = (key: string, defaultValue: string): string =>
      getTokenValue(`stats.${key}`, defaultValue);

    const getLabelValue = (key: string, defaultValue: string): string =>
      getTokenValue(`labels.${key}`, defaultValue);

    return {
      techLeaders: {
        value: getStatValue("techLeaders", DEFAULT_PLATFORM_STATS.techLeaders.value),
        label: getLabelValue("techLeaders", DEFAULT_PLATFORM_STATS.techLeaders.label)
      },
      capitalConnected: {
        value: getStatValue("capitalConnected", DEFAULT_PLATFORM_STATS.capitalConnected.value),
        label: getLabelValue("capitalConnected", DEFAULT_PLATFORM_STATS.capitalConnected.label)
      },
      countries: {
        value: getStatValue("countries", DEFAULT_PLATFORM_STATS.countries.value),
        label: getLabelValue("countries", DEFAULT_PLATFORM_STATS.countries.label)
      },
      speakers: {
        value: getStatValue("speakers", DEFAULT_PLATFORM_STATS.speakers.value),
        label: getLabelValue("speakers", DEFAULT_PLATFORM_STATS.speakers.label)
      },
      monthlyReaders: {
        value: getStatValue("monthlyReaders", DEFAULT_PLATFORM_STATS.monthlyReaders.value),
        label: getLabelValue("monthlyReaders", DEFAULT_PLATFORM_STATS.monthlyReaders.label)
      },
      industries: {
        value: getStatValue("industries", DEFAULT_PLATFORM_STATS.industries.value),
        label: DEFAULT_PLATFORM_STATS.industries.label
      },
      founders: {
        value: getStatValue("founders", DEFAULT_PLATFORM_STATS.founders.value),
        label: DEFAULT_PLATFORM_STATS.founders.label
      },
      investors: {
        value: getStatValue("investors", DEFAULT_PLATFORM_STATS.investors.value),
        label: DEFAULT_PLATFORM_STATS.investors.label
      },
      executives: {
        value: getStatValue("executives", DEFAULT_PLATFORM_STATS.executives.value),
        label: DEFAULT_PLATFORM_STATS.executives.label
      },
      articles: {
        value: getStatValue("articles", DEFAULT_PLATFORM_STATS.articles.value),
        label: DEFAULT_PLATFORM_STATS.articles.label
      },
      podcasts: {
        value: getStatValue("podcasts", DEFAULT_PLATFORM_STATS.podcasts.value),
        label: DEFAULT_PLATFORM_STATS.podcasts.label
      },
      summitAttendees: {
        value: getStatValue("summitAttendees", DEFAULT_PLATFORM_STATS.summitAttendees.value),
        label: DEFAULT_PLATFORM_STATS.summitAttendees.label
      },
      bookContributors: {
        value: getStatValue("bookContributors", DEFAULT_PLATFORM_STATS.bookContributors.value),
        label: DEFAULT_PLATFORM_STATS.bookContributors.label
      },
    };
  }, [tokens, isLoading, isError]);

  const getToken = (name: string, defaultValue = ""): string => {
    if (!tokens) return defaultValue;
    const token = tokens.find(t => t.name === name && t.isActive);
    return token?.staticValue || defaultValue;
  };

  return {
    tokens,
    isLoading,
    isError,
    getToken,
    platformStats,
  };
}
